import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStoreEvents } from '../../hooks/use-store-events';
import { resolveTaskView } from '../../core/task-views';
import { getTaskTeams } from '../../db/assignment-store';
import { listAuditLogs } from '../../db/audit-store';
import { getTaskProgress } from '../../db/task-store';
import { getTaskTimeline } from '../../db/timeline-store';
import { teamTimelineOf } from '../../core/timeline-engine';
import type { TaskTeam } from '../../types/assignment';
import type { AuditLogEntry } from '../../types/audit';
import type { Contact } from '../../types/contact';
import type { SoldierTaskProgress } from '../../types/execution';
import type { Soldier, Vehicle } from '../../types/fleet';
import type { Task, TaskType } from '../../types/task';
import type { TimelineEvent } from '../../types/timeline';
import type { User } from '../../types/user';
import { AccessDeniedView, ManagerTaskView, SoldierTaskView } from './views';

interface TaskDetailRouterProps {
  user: User;
  task: Task;
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  onBack: () => void;
  /** Opens the shared task form — managers only. */
  onEdit?: (task: Task) => void;
  /** Opens the shared vehicle-teams dialog — managers only. */
  onManageTeams?: (task: Task) => void;
  /** Raises the board's delete confirmation — managers only. */
  onDelete?: (task: Task) => void;
  /** Re-reads the board after anything was written here. */
  onReload: () => void | Promise<void>;
}

/**
 * The one way into a task's details, and the security boundary in front of it.
 *
 * Which screen an account gets is not decided here — `resolveTaskView()` in
 * `src/core/task-views.ts` answers that, and the store enforces the same answer
 * on every write. This component only loads what the chosen view needs and
 * renders it:
 *
 *   manager → {@link ManagerTaskView} · soldier on the crew → {@link SoldierTaskView}
 *   · anyone else → {@link AccessDeniedView}
 *
 * A soldier is handed exactly one team — their own — so the rest of the convoy
 * is not merely hidden from the screen, it never reaches it.
 */
export function TaskDetailRouter({
  user,
  task,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  onBack,
  onEdit,
  onManageTeams,
  onDelete,
  onReload,
}: TaskDetailRouterProps) {
  const [teams, setTeams] = useState<TaskTeam[]>([]);
  const [progress, setProgress] = useState<SoldierTaskProgress[]>([]);
  const [timeline, setTimeline] = useState<AuditLogEntry[]>([]);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [loadedTeams, loadedProgress, loadedTimeline, loadedEvents] = await Promise.all([
      getTaskTeams(task.id),
      getTaskProgress(task.id),
      listAuditLogs({ taskId: task.id }),
      getTaskTimeline(task.id),
    ]);
    setTeams(loadedTeams);
    setProgress(loadedProgress);
    setTimeline(loadedTimeline);
    setTimelineEvents(loadedEvents);
  }, [task.id]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const [loadedTeams, loadedProgress, loadedTimeline, loadedEvents] = await Promise.all([
        getTaskTeams(task.id),
        getTaskProgress(task.id),
        listAuditLogs({ taskId: task.id }),
        getTaskTimeline(task.id),
      ]);
      if (!alive) return;
      setTeams(loadedTeams);
      setProgress(loadedProgress);
      setTimeline(loadedTimeline);
      setTimelineEvents(loadedEvents);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [task.id]);

  /** Anything written from a view re-reads both this screen and the board. */
  const refresh = useCallback(async () => {
    await load();
    await onReload();
  }, [load, onReload]);

  // …and anything written from *outside* this screen re-reads it too. The
  // assignment dialog is owned by the board, not by the detail view, so a convoy
  // saved there used to leave this screen — and the dispatch order it builds —
  // holding the crew it had loaded on mount. The share button would then send an
  // image of an empty convoy for a task that had just been fully crewed.
  useStoreEvents(load, ['assignments', 'tasks', 'timeline', 'audit']);

  const view = useMemo(
    () => resolveTaskView(user, { teams, soldiers }),
    [user, teams, soldiers],
  );

  // Routing before the crew is known would refuse a soldier their own task.
  if (loading) return <p className="text-sm text-ink/50 py-8 text-center">טוען את פרטי המשימה…</p>;

  if (view.mode === 'manager') {
    return (
      <ManagerTaskView
        user={user}
        task={task}
        taskTypes={taskTypes}
        contacts={contacts}
        vehicles={vehicles}
        soldiers={soldiers}
        teams={teams}
        progress={progress}
        timeline={timeline}
        timelineEvents={timelineEvents}
        canEdit={view.canEdit}
        canAssign={view.canAssign}
        canClose={view.canClose}
        canViewInternalNotes={view.canViewInternalNotes}
        onBack={onBack}
        onEdit={onEdit}
        onManageTeams={onManageTeams}
        onDelete={onDelete}
        onReload={refresh}
      />
    );
  }

  if (view.mode === 'soldier' && view.team && view.crewRole && view.soldierId) {
    return (
      <SoldierTaskView
        user={user}
        task={task}
        taskTypes={taskTypes}
        contacts={contacts}
        vehicles={vehicles}
        soldiers={soldiers}
        team={view.team}
        soldierId={view.soldierId}
        crewRole={view.crewRole}
        // The convoy's other vehicles are filtered out before the feed is
        // handed over, exactly as the crew itself is.
        timelineEvents={teamTimelineOf(timelineEvents, view.team.id)}
        canReport={view.canReportFromField}
        onBack={onBack}
        onReload={refresh}
      />
    );
  }

  return <AccessDeniedView reason={view.reason} message={view.message} onBack={onBack} />;
}
